import { likeCrowdExp, playCrowdExp } from 'api/crowdExpressions';
import { React, useEffect, useRef, useState } from 'react';
import { formatDuration } from 'utils/formatDuration';
import { formatStat } from 'utils/formatStat';

const CrowdExpressionItem = ({
  item,
  isActive,
  onRegisterAudio,
  onToggle,
  onPause,
  audioProgress,
  audioCurrentTime,
  waveformData,
}) => {
  const audioRef = useRef(null);
  const [isLiked, setIsLiked] = useState(item.liked);

  useEffect(() => {
    onRegisterAudio(item.id, audioRef.current);

    return () => {
      onRegisterAudio(item.id, null);
    };
  }, [item.id, onRegisterAudio]);

  useEffect(() => {
    setIsLiked(item.liked);
  }, [item.liked]);

  const handlePlay = async () => {
    if (isActive) {
      onPause(item.id);
      return;
    }

    try {
      if (!item.played) {
        const result = await playCrowdExp(item.id);
        if (result) {
          item.played = true;
          item.plays++;
        }
      }
    } catch (error) {
      console.error('Failed to play crowd expression:', error);
    }
    onToggle(item);
  };

  const handleLike = async () => {
    if (item.liked) return;

    try {
      if (!item.liked) {
        const result = await likeCrowdExp(item.id);

        if (result) {
          setIsLiked(true);
        }
      }
    } catch (error) {
      console.error('Failed to like crowd expression:', error);
    }
  };

  return (
    <li className="min-w-0 overflow-hidden rounded-2xl border border-white/10 bg-slate-800/90 text-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.24)] transition-all duration-300 ease-out hover:-translate-y-px hover:border-white/15">
      <article className="relative min-w-0 p-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <img
              src="/images/makan_tumb.webp"
              alt={item.name}
              loading="lazy"
              decoding="async"
              className="block h-14 w-14 flex-none rounded-[14px] object-cover"
            />

            <div className="min-w-0 flex-[0_1_auto]">
              <h3 className="m-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold leading-6">
                {item.name}
              </h3>

              <div className="flex items-center gap-2 whitespace-nowrap text-[13px] text-slate-400">
                <span>از {item.city}</span>
              </div>
            </div>

            <div className="min-w-0 flex-1 pl-2 md:ml-auto md:flex md:justify-end">
              <div className="relative h-10 w-full overflow-hidden rounded-[10px] bg-white/5">
                <div className="absolute inset-0 flex items-end gap-[2px] px-2 py-[6px] opacity-45">
                  {waveformData.map((value, index) => (
                    <span
                      key={index}
                      className="min-w-[2px] flex-[1_1_0] rounded-full bg-white/45"
                      style={{
                        height: `${Math.max(8, value / 4)}px`,
                        opacity: value / 255,
                      }}
                    />
                  ))}
                </div>

                <div
                  className="absolute inset-y-0 left-0 border-r border-white/25 bg-white/10"
                  style={{ width: `${audioProgress * 100}%` }}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <span className="whitespace-nowrap text-[13px] text-slate-300">
              {!isActive
                ? formatDuration(item.duration)
                : formatDuration(audioCurrentTime)}
            </span>

            <button
              type="button"
              onClick={handlePlay}
              aria-label={isActive ? `Pause ${item.name}` : `Play ${item.name}`}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/10 text-inherit transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isActive ? (
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  width="20"
                  height="20"
                >
                  <rect
                    x="7"
                    y="5"
                    width="4"
                    height="14"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    x="13"
                    y="5"
                    width="4"
                    height="14"
                    rx="1"
                    fill="currentColor"
                  />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  width="20"
                  height="20"
                >
                  <path d="M8 5v14l11-7z" fill="currentColor" />
                </svg>
              )}
            </button>

            <button
              type="button"
              disabled
              className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-inherit"
            >
              ↗ <span>{formatStat(item.plays)}</span>
            </button>

            <button
              type="button"
              onClick={handleLike}
              disabled={isLiked}
              aria-pressed={isLiked}
              aria-label={isLiked ? `Liked ${item.name}` : `Like ${item.name}`}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-2 text-inherit transition ${
                isLiked
                  ? 'border-rose-400/30 bg-rose-500/15 text-rose-200'
                  : 'border-white/10 bg-white/5 hover:bg-white/10'
              } disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {isLiked ? '♥' : '♡'}{' '}
              <span>
                {formatStat(item.likes + (isLiked && !item.liked ? 1 : 0))}
              </span>
            </button>
          </div>
        </div>

        <audio ref={audioRef} preload="none" hidden crossOrigin="anonymous" />
      </article>
    </li>
  );
};

export default CrowdExpressionItem;
